import { sessionDateOf } from './replayStats';
import { dualLedgerReducer, executeBuy, executeSell, flattenPosition } from './dualLedger';

/**
 * FEEDING THE PLAYHEAD INTO THE DUAL LEDGER: the four pure functions the component needs.
 *
 * <p>Everything here is a function of (timeline, index) or (state, action). None of it needs a
 * chart, a DOM or a clock — which is the whole reason it is not inside the component, where the
 * only way to test it would be to drive a replay through jsdom and assert on a mock.
 */

/**
 * The bar the playhead is on, in the shape the ledger fills at.
 *
 * <p>`close` and nothing else: a discretionary order fills at the close of the bar being looked
 * at, so the open, high and low are not inputs to anything here and carrying them would invite a
 * later rule to reach for one.
 *
 * <p>The date is STRING-SLICED from the engine's own timestamp, never parsed through `Date`. An
 * engine timestamp is an offset date-time in the exchange's own offset, and converting via UTC
 * moves a `19:30-05:00` bar onto the NEXT candle — which survives a casual look because it only
 * misfires on late sessions, and would put a human fill on a bar the operator never saw.
 */
export function barAt(timeline, index) {
  const frame = (timeline?.frames || [])[index];
  if (!frame) return null;
  const date = sessionDateOf(frame.time);
  if (!date || !Number.isFinite(frame.close)) return null;
  return { date, close: frame.close, timeframe: frame.timeframe, state: frame.state };
}

/**
 * What the algo holds at this bar, and what it last held.
 *
 * <p>Both, because `MATCH_ALGO` sizing needs the second one: the engine is FLAT on most bars, and
 * an operator pressing BUY while the rules stand aside is exactly when a copilot is interesting.
 * Refusing there would disable the feature precisely when it has something to say, so the ledger
 * inherits the most recent allocation and records that it did.
 *
 * <p>Scanned on demand rather than tracked per tick. A key press is rare and a scan of 8,466
 * frames is microseconds; a running tally would be state that has to be rewound when the transport
 * steps backwards, and a tally that forgot to rewind would size a trade from the algo's FUTURE.
 */
export function algoSharesAt(timeline, index) {
  const frames = timeline?.frames || [];
  const upTo = Math.min(index, frames.length - 1);
  let live = 0;
  let last = 0;
  for (let i = 0; i <= upTo; i++) {
    const shares = frames[i]?.account?.openShares;
    if (Number.isFinite(shares) && Math.abs(shares) > 0) {
      last = Math.abs(shares);
      if (i === upTo) live = Math.abs(shares);
    }
  }
  return { algoShares: live, lastAlgoShares: last };
}

/**
 * The engine's CLOSED trades up to the playhead.
 *
 * <p>Truncated, and that is the point: a comparison of the full algo book against a human book
 * that has traded four bars is not a comparison of anything. `frameIndex` on a stamped trade is
 * its EXIT frame, so this is "trades the engine has finished by now" — the same bound every other
 * annotation in the replay uses.
 *
 * <p>The OPEN premise is excluded, here as everywhere: its number is a mark, not a result, and on
 * this rule set one open trade can be worth more than every closed one together.
 */
export function algoTradesUpTo(timeline, index) {
  const stamped = Array.isArray(timeline?.trades) ? timeline.trades : [];
  return stamped
    .filter((s) => s && s.trade && Number.isInteger(s.frameIndex)
      && s.frameIndex >= 0 && s.frameIndex <= index)
    .map((s) => s.trade);
}

/**
 * THE OPEN DISCRETIONARY POSITION, MARKED AT THE PLAYHEAD BAR.
 *
 * <h2>Why the bar is fed in HERE and not into the reducer</h2>
 *
 * The brief asks for the current bar to reach the state machine on every tick. It does — through
 * this function, which every tick calls — but the LEDGER is deliberately not advanced by it, and
 * the reason is the transport's Arrow Left.
 *
 * <p>A reducer that consumed bars would have to be REWOUND when the playhead steps back, and a
 * rewind that missed anything would leave the human book carrying a mark from a bar the operator
 * has stepped away from. Keeping the ledger a pure function of ACTIONS makes stepping backwards
 * correct by construction: the book at bar 400 is the same book however many times you walked
 * past it. The mark is then derived from whatever bar is current, which is what a mark is.
 *
 * <p>Costs are deliberately NOT applied. An open position has paid its entry commission and will
 * pay an exit one, and charging either here would make the mark disagree with the realised figure
 * the moment it closes. It is the unrealised PRICE move, which is the only thing a mark can
 * honestly be.
 *
 * @returns {{price, unrealisedPnl, unrealisedPercent}|null} null when there is nothing open
 */
export function markAt(position, bar) {
  if (!position || !bar || !Number.isFinite(bar.close)) return null;
  if (!Number.isFinite(position.entryPrice) || position.entryPrice === 0) return null;
  const sign = position.direction === 'SHORT' ? -1 : 1;
  const move = (bar.close - position.entryPrice) * sign;
  return {
    price: bar.close,
    unrealisedPnl: move * (Number.isFinite(position.shares) ? position.shares : 0),
    unrealisedPercent: (move / position.entryPrice) * 100,
  };
}

/**
 * Apply a human action at the playhead.
 *
 * <p>One place where an action name becomes a ledger call, so the HUD buttons and the hotkeys
 * cannot diverge — they both arrive here with the same three-word vocabulary. The alternative was
 * a switch in the component and another in the key handler, which is two places to add the fourth
 * action to.
 *
 * @param state the dual state
 * @param kind  'BUY' | 'SELL' | 'FLAT'
 * @param timeline the run, for the bar and the algo's size
 * @param index the playhead
 * @returns a NEW dual state — unchanged, apart from a recorded refusal, when the action cannot run
 */
export function applyHumanAction(state, kind, timeline, index) {
  const bar = barAt(timeline, index);
  if (!bar) {
    // REFUSED THROUGH THE REDUCER rather than returned early, so the reason reaches the HUD by
    // the same route every other refusal does. A silent no-op is indistinguishable from a broken
    // button, which is the one thing a control the operator is learning must not be.
    return flattenPosition(state, null);
  }
  const { algoShares, lastAlgoShares } = algoSharesAt(timeline, index);
  const extra = { algoShares, lastAlgoShares };

  if (kind === 'BUY') return executeBuy(state, bar, extra);
  if (kind === 'SELL') return executeSell(state, bar, extra);
  if (kind === 'FLAT') return flattenPosition(state, bar);
  // An unrecognised action is REFUSED by name, not quietly treated as the nearest one. The
  // reducer already answers "unknown action X", and that message is far more use than a trade
  // nobody asked for.
  return dualLedgerReducer(state, { type: kind, bar });
}
